import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Loader2, Share2 } from 'lucide-react';
import { BRAND } from '../config/brand';
import { GARDEN_BACKGROUNDS, type GardenMood } from '../config/garden';
import { lunarToday } from '../../shared/lunar';
import { renderLeafCard } from '../lib/leafCard';

type Props = { gardenName: string; mood: GardenMood; color: number; onClose: () => void };

/** "Một chiếc lá cho bạn" (AGENTS.md §7.4): keep the leaf or send it to family. Made on this device; never counted. */
export function LeafGift({ gardenName, mood, color, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [canShare, setCanShare] = useState(false);
  const file = useRef<File | null>(null);
  useEffect(() => {
    let alive = true, made: string | null = null;
    const now = new Date();
    const lunar = lunarToday(now);
    const month = t(`lunar.months.${lunar.month}`);
    const day = lunar.day <= 10 ? t('lunar.early', { n: lunar.day }) : String(lunar.day);
    const text = {
      garden: gardenName, line: t('leaf.line'), domain: BRAND.domain,
      date: new Intl.DateTimeFormat(i18n.language, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(now),
      lunar: t('lunar.date', { day, month: lunar.leap ? t('lunar.leap', { month }) : month }),
    };
    renderLeafCard(text, color, GARDEN_BACKGROUNDS[mood].portrait).then(blob => {
      if (!alive) return;
      const name = `la-bo-de-${now.toISOString().slice(0, 10)}.png`;
      file.current = new File([blob], name, { type: 'image/png' });
      made = URL.createObjectURL(blob); setUrl(made);
      try { setCanShare(typeof navigator.canShare === 'function' && navigator.canShare({ files: [file.current] })); } catch { setCanShare(false); }
    }).catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; if (made) URL.revokeObjectURL(made); };
  }, [gardenName, mood, color, t, i18n.language]);

  function keep() {
    if (!url || !file.current) return;
    const link = document.createElement('a'); link.href = url; link.download = file.current.name; link.click();
  }
  async function share() {
    if (!file.current) return;
    try { await navigator.share({ files: [file.current], title: BRAND.name, text: t('leaf.shareText', { domain: BRAND.domain }) }); } catch { /* the visitor closed the share sheet */ }
  }
  return <div className="leaf-gift">
    <p className="modal-description">{t('leaf.description')}</p>
    <div className="leaf-card">{url ? <img src={url} alt={t('leaf.alt')} data-testid="leaf-card" /> : failed ? <p>{t('leaf.failed')}</p> : <Loader2 className="spin" size={22} aria-label={t('leaf.making')} />}</div>
    <div className="modal-buttons">
      <button type="button" className="primary-button" onClick={keep} disabled={!url}><Download size={17} />{t('leaf.keep')}</button>
      {canShare && <button type="button" className="secondary-button" onClick={() => void share()}><Share2 size={17} />{t('leaf.share')}</button>}
    </div>
    <button type="button" className="text-button leaf-leave" onClick={onClose}>{t('leaf.leave')}</button>
    <p className="modal-note">{t('leaf.note')}</p>
  </div>;
}
